/**
 * « Recadrer l'image » avant que la photo ne soit lue pour générer une activité, au web : l'étape de
 * `ImageCropModal.web.js`, pour les deux entrées du tuteur qui génèrent depuis une photo, hors live
 * (`useTutorGenerateActivity`) et dans la salle (`useTutorLiveFeedGeneration`). L'élève et le parent n'en avaient pas : ils n'appellent pas ce crochet.
 *
 *  - `hold(file, meta)` : une photo, au web, est retenue (rend `true`) ; un PDF, et tout au téléphone, ne l'est
 *    pas (`false`) — l'appelant la joint tel quel. C'est le `holdFile` du parcours partagé, et ce que le dépôt
 *    ou le collage de la salle appellent avant de joindre.
 *  - `step` : les props de `<TutorImageCropStep>` tant qu'une photo attend, `null` sinon. La zone part de la
 *    photo entière et suit le geste ; « Valider » joint la zone découpée (`cropImageFile`), « Passer » la photo
 *    entière, une découpe qui échoue aussi ; la croix abandonne. Une découpe qui finit après la croix, ou après
 *    qu'une autre photo a pris la place, ne joint rien.
 *  - `clear()` oublie la photo qui attend (une nouvelle ouverture du parcours) ;
 *  - les adresses des JPEG découpés sont libérées quand l'écran se démonte.
 *
 * `attach` est lu au moment de joindre : l'appelant peut le définir après ce crochet dans son rendu (il joint
 * par le parcours partagé, créé plus bas avec `hold` pour `holdFile`).
 */
import { useCallback, useEffect, useRef, useState } from "react"

import { isPdfFile } from "../../../../data/utility/pdfHelpers"
import { IS_WEB } from "../../../../engine/shared/platform"
import { type TutorImageCropStepProps } from "../../../../design-system/templates/TutorImageCropStep/TutorImageCropStep"
import { FULL_CROP_AREA } from "../formatters/cropAreaFormatter"
import { type UIActivityFile } from "../types/activityGeneration"
import { type UITutorCropArea } from "../types/tutorEleves"
import { cropImageFile } from "./cropImageFile"

/** Une photo, pas un PDF : le recadrage ne vaut que pour elle. Le type voyage avec le fichier (`isPdfFile`). */
export const isCroppableImage = (file: UIActivityFile) =>
    file.mimeType != null ? file.mimeType.startsWith("image/") : !isPdfFile(file)

type Pending<M> = { file: UIActivityFile; meta: M | undefined; area: UITutorCropArea; processing: boolean }

export type TutorImageCrop<M> = {
    hold: (file: UIActivityFile, meta?: M) => boolean
    clear: () => void
    step: Omit<TutorImageCropStepProps, "testID"> | null
}

export function useTutorImageCrop<M = undefined>(
    attach: (file: UIActivityFile, meta: M | undefined) => void
): TutorImageCrop<M> {
    const attachRef = useRef(attach)
    attachRef.current = attach
    const [pending, setPending] = useState<Pending<M> | null>(null)
    const pendingRef = useRef(pending)
    pendingRef.current = pending

    const disposersRef = useRef<(() => void)[]>([])
    useEffect(
        () => () => {
            for (const dispose of disposersRef.current) dispose()
        },
        []
    )

    const hold = useCallback((file: UIActivityFile, meta?: M) => {
        if (!IS_WEB || !isCroppableImage(file)) return false
        setPending({ file, meta, area: FULL_CROP_AREA, processing: false })
        return true
    }, [])

    const clear = useCallback(() => setPending(null), [])

    if (pending == null) return { hold, clear, step: null }

    const join = (file: UIActivityFile) => {
        setPending(null)
        attachRef.current(file, pending.meta)
    }
    return {
        hold,
        clear,
        step: {
            visible: true,
            uri: pending.file.uri,
            area: pending.area,
            processing: pending.processing,
            // La zone se gèle pendant la découpe : `<TutorImageCropStep>` coupe alors le geste.
            onChangeArea: (area) => setPending((current) => (current != null ? { ...current, area } : current)),
            onSkip: () => join(pending.file),
            onClose: () => setPending(null),
            onConfirm: () => {
                setPending({ ...pending, processing: true })
                void cropImageFile(pending.file, pending.area).then((cropped) => {
                    if (cropped != null) disposersRef.current.push(cropped.dispose)
                    // Fermée, ou remplacée par une autre photo pendant la découpe : rien ne se joint.
                    if (pendingRef.current?.file !== pending.file) return
                    join(cropped?.file ?? pending.file)
                })
            },
        },
    }
}
